import React, { useCallback, useMemo, useState } from 'react';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import Icon, { SettingFilled } from '@ant-design/icons';
import TemplateStringWidgetProcessor, {
	TemplateStringWidgetState,
	getDefaultState
} from '@kemu-io/kemu-core/widgets/templateString/index.js';
import { Dropdown } from 'antd';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import { WidgetPort } from '@kemu-io/hs-types';
import formatHtml from 'html-format';
import classNames from 'classnames';
import GateIcon from '../../gateIcon/gateIcon';
import { ReactComponent as AiAssistedIcon } from '@src/assets/img/ai-assisted-icon.svg';
import {
	GetPortsInformationFunction,
	GateUI,
	GateUIProps,
} from '../index.ts';
import { PortLocation } from '../../../types/canvas_t';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import { ABORT_DRAGGING_CLASS } from '../../../common/constants';
import 'react-resizable/css/styles.css';
import TextCreationAssistant from '../../TextCreationAssistant';
import TemplateEditor from '../../TemplateEditor/TemplateEditor';
import { ReactComponent as TemplateStringWidgetIcon } from './icon.svg';
import styles from './templateString.module.css';
import useTranslation from '@hooks/useTranslation.ts';


const DEFAULT_W = 220;
const DEFAULT_H = 100;

// Formatting functions
const formatJSON = (text: string): string => {
	try {
		const parsed = JSON.parse(text);
		return JSON.stringify(parsed, null, 2);
	} catch (error) {
		console.warn('Invalid JSON for formatting:', error);
		return text; // Return original text if parsing fails
	}
};


const formatText = (text: string): string => {
	return text.trim();
};


const TemplateStringWidget = (props: GateUIProps): React.JSX.Element => {
	const { repaintExistingPorts, recipeId, thingRecipeId, info } = props;
	const [state, setState] = useReactiveWidgetState<TemplateStringWidgetState>(recipeId, thingRecipeId, info.id, );
	const [localText, setLocalText] = useState(state.text || '');
	const t = useTranslation('LogicMapper.Widgets.TemplateString');
	const [showTextAssistant, setShowTextAssistant] = useState(false);

	const fixedState: TemplateStringWidgetState = useMemo(() => ({
		...getDefaultState(),
		...state
	}), [state]);

	const handleResize = useCallback((event: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
		setState((current) => ({
			...current,
			size: {
				width: data.size.width,
				height: data.size.height
			}
		}));
		repaintExistingPorts();
	}, [repaintExistingPorts, setState]);

	const handleTextChange = (newText: string) => {
		setLocalText(newText);
		setState({ ...fixedState, text: newText }, false);
	};

	const handleTextGenerated = (generatedText: string) => {
		setLocalText(generatedText);
		setState({ ...fixedState, text: generatedText }, true);
	};

	const formatNow = (format: 'Text' | 'JSON' | 'HTML') => {
		let formattedText = localText;
		switch (format) {
			case 'JSON':
				formattedText = formatJSON(localText);
				break;
			case 'HTML':
				formattedText = formatHtml(localText);
				break;
			case 'Text':
			default:
				formattedText = formatText(localText);
				break;
		}
		setLocalText(formattedText);
	};

	const handleFormatSelection = (format: 'Text' | 'JSON' | 'HTML') => {
		setState({ ...fixedState, format: format });
		formatNow(format);
	};


	return (
		<div className={styles.TemplateStringWidgetContainer}>
			<div className={styles.Header}>
				<div className={styles.HeaderLabel}>
					<span>{t('Title')}</span>
				</div>
				<div className={styles.AssistantBtn} onClick={() => setShowTextAssistant(true)}>
					<Icon component={AiAssistedIcon} />
				</div>
			</div>

			<Resizable
				height={fixedState.size?.height || DEFAULT_H}
				width={fixedState.size?.width || DEFAULT_W}
				minConstraints={[DEFAULT_W, DEFAULT_H]}

				onResize={handleResize}
				handle={<div className="react-resizable-handle react-resizable-handle-se" style={{ zIndex: 1 }} />}
			>
				<div className={`${styles.GateBody} ${ABORT_DRAGGING_CLASS}`}
					style={{
						width: (fixedState.size?.width || DEFAULT_W),
						height: (fixedState.size?.height || DEFAULT_H),
						position: 'relative' // Needed for absolute positioning of children
					}}
				>
					<TemplateEditor
						value={localText}
						onChange={handleTextChange}
						disabled={props.info.disabled}
					/>

					<div className={classNames(styles.FormatDropdownContainer, { [styles.Disabled]: props.info.disabled })}>
						<Dropdown.Button
							type='text'
							size='small'
							trigger={['click']}
							className={styles.FormatDropdownButton}
							onClick={() => handleFormatSelection(fixedState.format || 'Text')}
							menu={{
								onClick: (event) => handleFormatSelection(event.key as 'Text' | 'JSON' | 'HTML'),
								items: [
									{ key: 'Text', label: 'Text' },
									{ key: 'JSON', label: 'JSON' },
									{ key: 'HTML', label: 'HTML' },
								],
							}}
						>
							{t('FormatBtn', { format: fixedState.format || 'Text' })}
						</Dropdown.Button>
					</div>
				</div>
			</Resizable>

			{showTextAssistant && (
				<TextCreationAssistant
					show={showTextAssistant}
					currentText={localText}
					onTextGenerated={handleTextGenerated}
					onCancel={() => setShowTextAssistant(false)}
				/>
			)}
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={TemplateStringWidgetIcon} />}/>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state: WidgetState<TemplateStringWidgetState>, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = TemplateStringWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = TemplateStringWidgetProcessor.getInputNames(state, portContext);

	const inputPositions: Record<string, PortLocation> = {
		[inputNames[0].name]: [0, 0.5, -1, 0],
	};

	const outputPositions: Record<string, PortLocation> = {
		[outputNames[0].name]: [1, 0.5, 1, 0],
	};

	return {
		inputs: inputNames.map((input: WidgetPort) => ({
			name: input.name,
			type: input.type,
			position: inputPositions[input.name] || 'Left',
			label: input.name
		})),

		outputs: outputNames.map((output: WidgetPort) => ({
			name: output.name,
			type: output.type,
			position: outputPositions[output.name] || 'Right',
			label: output.name
		})),
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: TemplateStringWidget,
	getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.TemplateString.Title' }),
	hasTitle: false,
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.TemplateString.Title' }),
} as GateUI;
